import { Component, h } from 'preact';
import { Subscription, fromEvent } from 'rxjs';
import { map, tap, distinctUntilChanged } from 'rxjs/operators';
import { distinctUntilChangedImmutable } from 'distinct-until-changed-immutable';

import getStore, { appState$ } from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

import { Inquiry, InquiryFTF, InquiryDocument, ViewingListMetadata } from '../../../../../types/inquiries';
import { InquiryDetailsCard } from './inquiry-details-card.component';
import { ListResponse, Listing, ReqState } from '../../../../../types';
import { Comment } from '../../../../../types/comment.model';
import { LayoutBreakpoints } from '../../../../../constants';
import { InquiryDetailsMobile } from './inquiry-details-mobile';


const store = getStore();

interface InquiryDetailsCardHostState {
  viewportWidth?: number;

  selectedInquiry?: Inquiry;
  inquiryDetailsVisible?: boolean;

  ftf?: InquiryFTF;
  comments?: ListResponse<Comment>;
  selectedProperties?: ListResponse<Listing>;
  viewingLists?: ListResponse<ViewingListMetadata>;
  offers?: ListResponse<InquiryDocument>;
  contracts?: ListResponse<InquiryDocument>;

  ftfReqState?: ReqState;
  commentsReqState?: ReqState;
  selectedPropertiesReqState?: ReqState;
  viewingListsReqState?: ReqState;
  offersReqState?: ReqState;
  contractsReqState?: ReqState;
}

export class InquiryDetailsCardHost extends Component<any, InquiryDetailsCardHostState> {
  subscriptions: Subscription[] = [];

  componentDidMount() {
    this.setState({ viewportWidth: window.innerWidth });

    const inquiries$ = appState$.pipe(map(app => app.inquiries));

    this.subscriptions.push(
      inquiries$.pipe(
        map(
          ({ selectedInquiry, inquiryDetailsVisible, ftf, comments, selectedProperties, viewingLists, offers, contracts,
            ftfReqState, commentsReqState, selectedPropertiesReqState, offersReqState, viewingListsReqState, contractsReqState }) =>
            ({ selectedInquiry, inquiryDetailsVisible, ftf, comments, selectedProperties, viewingLists, offers, contracts,
              ftfReqState, commentsReqState, selectedPropertiesReqState, viewingListsReqState, offersReqState, contractsReqState })
        ),
        distinctUntilChangedImmutable(), // TODO distinctUntilChangedImmutable does not work here
      ).subscribe(s => this.setState(s))
    );

    this.subscriptions.push(
      fromEvent(window, 'resize').pipe(
        map(event => event.target as Window),
        map(window => window.innerWidth),
        distinctUntilChanged()
      ).subscribe(width => this.onWindowWidthChange(width))
    );
  }

  onWindowWidthChange(width: number) {
    this.setState({ viewportWidth: width });
  }

  wireToDialog(dialog) {
    return (selectedInquiry: Inquiry, documentForEditing?: InquiryDocument) => {
      store.dispatch(InquiriesActions.showModal(dialog, { selectedInquiry, documentForEditing }));
    }
  }

  showEditFtfDialog(selectedInquiry: Inquiry, ftfForEditing: InquiryFTF) {
    store.dispatch(InquiriesActions.showModal('edit-ftf', { selectedInquiry, ftfForEditing }));
  }

  onEditVl(inquiry: Inquiry, selectedVlKey: string) {
    store.dispatch(InquiriesActions.showModal('create-vl', { selectedVlKey }));
  }

  onClose() {
    store.dispatch(InquiriesActions.closeDetailsCard())
  }

  render(props, state: InquiryDetailsCardHostState) {
    if (state.inquiryDetailsVisible) {
      const detailsCard = (
        <InquiryDetailsCard inquiry={ state.selectedInquiry }
          mobileMode={ state.viewportWidth < LayoutBreakpoints.lg.min }
          ftf={ state.ftf }
          comments={ state.comments }
          selectedProperties={ state.selectedProperties }
          viewingLists={ state.viewingLists }
          offers={ state.offers }
          contracts={ state.contracts }

          ftfReqState={ state.ftfReqState }
          commentsReqState={ state.commentsReqState }
          selectedPropertiesReqState={ state.selectedPropertiesReqState }
          viewingListReqState={ state.viewingListsReqState }
          offersReqState={ state.offersReqState }
          contractsReqState={ state.contractsReqState }

          onEditFTF={ (inquiry, ftf) => this.showEditFtfDialog(inquiry, ftf) }

          onCreateVl={ this.wireToDialog('create-vl') }
          onEditVl={ (inquiry, vlKey) => this.onEditVl(inquiry, vlKey) }

          onCreateLoiClick={ this.wireToDialog('create-loi') }
          onEditLoiClick={ this.wireToDialog('edit-loi') }
          onEditCommissionClick={ this.wireToDialog('edit-commission-card') }
          onUploadOfferSentClick={ this.wireToDialog('upload-offer-sent') }
          onUploadOfferGenericClick={ this.wireToDialog('upload-offer-generic') }
          onSentOfferStatusClick={ this.wireToDialog('offer-status') }
          onCreateContractClick={ this.wireToDialog('create-contract') }
          onEditContractClick={ this.wireToDialog('edit-contract')  }
          onUploadContractClick={ this.wireToDialog('upload-contract') }

          onShowCommentsModal={ this.wireToDialog('comments') }
          onClose={ () => this.onClose() } />
      );

      if (state.viewportWidth < LayoutBreakpoints.lg.min) {
        return (
          <InquiryDetailsMobile>
            { detailsCard }
          </InquiryDetailsMobile>
        );
      } else {
        return detailsCard;
      }
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}
